import { Action, State, StateContext } from "@ngxs/store";
import { CategoriaFiltroDTO, TagDTO, TagInfoDTO } from "@DTOS";
import { Inject, Injectable, LOCALE_ID } from "@angular/core";
import { TagsService } from "@shared/servicesBackend";
import { EtiquetasAcciones } from "./etiquetas.acciones";
import { CustomState } from "../CustomState";
import { EstadoEnum } from "../entidades";
import { TraduccionesToken } from "@shared/tokens";
import { Traducciones } from "@locale";
import { formatTraducir, Nilable } from "@shared/utils";
import { EstadoState } from "../EstadoState";
import { AccionEnum } from "@shared/enums";

export interface IEtiquetasState extends EstadoState<CategoriaFiltroDTO | TagDTO> {
    categorias: CategoriaFiltroDTO[];
    categoriasSeleccionadas: Nilable<CategoriaFiltroDTO[]>;
    etiquetas: TagDTO[];
    etiquetaInfo: Nilable<TagInfoDTO>;
}

@State<IEtiquetasState>({
    name: "etiquetas",
    defaults: {
        etiquetas: [],
        categorias: [],
        categoriasSeleccionadas: null,
        etiquetaInfo: null,
        estadoAccion: null
    }
})
@Injectable()
export class EtiquetasState extends CustomState<IEtiquetasState> {
    private categorias: CategoriaFiltroDTO[];
    private tags: TagDTO[];

    private cargo = false;

    constructor(private tagsService: TagsService, @Inject(TraduccionesToken) private traducciones: Traducciones,
        @Inject(LOCALE_ID) private localeId: string) {
        super();
    }

    @Action(EtiquetasAcciones.ObtenerTodo)
    public async obtener(ctx: StateContext<IEtiquetasState>) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (!this.cargo) {
            this.categorias = await this.tagsService.obtenerCategorias();
            const tags = await this.tagsService.obtenerTags();
            this.tags = tags.map(tag => {
                tag.tipo = this.categorias.find(categoria => categoria.id === tag.tipo.id) ?? new CategoriaFiltroDTO();
                return tag;
            });
            this.cargo = true;
        }

        nuevoState.categorias = [...this.categorias];
        nuevoState.etiquetas = [...this.tags];

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ObtenerCategorias)
    public async obtenerCategorias(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ObtenerCategorias) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (!this.cargo) {
            await this.obtener(ctx);
        }

        nuevoState.categoriasSeleccionadas = this.categorias.filter(categoriaFiltro =>
            action.nombres.includes(categoriaFiltro.nombre)
        );

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ObtenerCategoriasRestantes)
    public async obtenerCategoriasRestantes(
        ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ObtenerCategoriasRestantes
    ) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (!this.cargo) {
            await this.obtener(ctx);
        }

        nuevoState.categoriasSeleccionadas = this.categorias.filter(categoriaFiltro =>
            !action.nombres.includes(categoriaFiltro.nombre)
        );

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ObtenerTagsPorCategorias)
    public async obtenerTagsPorCategoria(
        ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ObtenerTagsPorCategorias
    ) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (!this.cargo) {
            await this.obtener(ctx);
        }

        nuevoState.etiquetas = this.tags.filter(tag => action.nombres.includes(tag.tipo.nombre));

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ObtenerTagsAutocomplete)
    public async obtenerTagsPorAutocomplete(
        ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ObtenerTagsAutocomplete
    ) {
        const nuevoState = this.obtenerNuevoState(ctx);

        if (!this.cargo) {
            await this.obtener(ctx);
        }

        nuevoState.etiquetas = this.tags.filter(tag => this.esCoincidencia(action.nombre, tag));

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ObtenerTagInfo)
    public async obtenerTagInfo(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ObtenerTagInfo) {
        const nuevoState = this.obtenerNuevoState(ctx);

        nuevoState.etiquetaInfo = await this.tagsService.obtenerTagInfo(action.tag);

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.AgregarCategoria)
    public async agregarCategoria(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.AgregarCategoria) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const categoriaNueva = await this.tagsService.agregarTagType(action.categoriaFiltro);
        if (!this.cargo) {
            this.categorias = [categoriaNueva];
        } else {
            this.categorias = [...this.categorias, categoriaNueva];
        }

        nuevoState.categorias = [...this.categorias];
        nuevoState.estadoAccion = { estado: EstadoEnum.CORRECTO, accion: AccionEnum.AGREGAR, entidad: categoriaNueva };

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.AgregarTag)
    public async agregarTag(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.AgregarTag) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const nuevoTag = await this.tagsService.agregarTag(action.etiqueta);

        if (!this.cargo) {
            this.tags = [nuevoTag];
        } else {
            this.tags = [...this.tags, nuevoTag];
        }

        nuevoState.etiquetas = [...this.tags];
        nuevoState.estadoAccion = { estado: EstadoEnum.CORRECTO, accion: AccionEnum.AGREGAR, entidad: nuevoTag };

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ModificarTag)
    public async modificaragregarTag(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.ModificarTag) {
        const nuevoState = this.obtenerNuevoState(ctx);

        const tagModificado = await this.tagsService.modificarTag(action.etiqueta);

        if (!this.cargo) {
            this.tags = [tagModificado];
        } else {
            this.tags = this.tags.map(tag => tag.id === tagModificado.id ? tagModificado : tag);
        }

        nuevoState.etiquetas = [...this.tags];
        nuevoState.estadoAccion = {
            estado: EstadoEnum.CORRECTO, accion: AccionEnum.ACTUALIZAR, entidad: tagModificado
        };

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ReiniciarEstadoAccion)
    public reiniciarEstadoAccion(ctx: StateContext<IEtiquetasState>) {
        const nuevoState = this.obtenerNuevoState(ctx);

        nuevoState.estadoAccion = null;

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ReiniciarCategoriasSeleccionadas)
    public reiniciarCategoriasSeleccionadas(ctx: StateContext<IEtiquetasState>) {
        const nuevoState = this.obtenerNuevoState(ctx);
        nuevoState.categoriasSeleccionadas = null;
        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.ReiniciarEtiquetaInfo)
    public reiniciarEtiquetaInfo(ctx: StateContext<IEtiquetasState>) {
        const nuevoState = this.obtenerNuevoState(ctx);

        nuevoState.etiquetaInfo = null;

        ctx.setState(nuevoState);
    }

    @Action(EtiquetasAcciones.PedirEntidades)
    public async pedirEntidades(ctx: StateContext<IEtiquetasState>, action: EtiquetasAcciones.PedirEntidades) {
        if (!this.cargo) {
            await this.obtener(ctx);
        }

        ctx.dispatch(new action.accion(this.tags));
    }

    /**
     * Verifica si el texto esta incluido en el valor de la etiqueta
     *
     * @param texto Texto a verificar si esta incluido
     * @param tag Etiqueta donde verificar
     * @private
     */
    private esCoincidencia(texto: Nilable<string>, tag: TagDTO): boolean {
        if (texto == null) {
            return false;
        }
        const regExpIncluye = new RegExp(texto.sacarTildes(), "i");
        let esCoincidencia = false;

        if (tag.valor != null) {
            esCoincidencia = regExpIncluye.test(tag.valor) ||
                regExpIncluye.test(formatTraducir(this.traducciones, tag.valor, this.localeId));
        }

        if (!esCoincidencia && tag.tipo != null) {
            esCoincidencia = regExpIncluye.test(tag.tipo.nombre) ||
                regExpIncluye.test(formatTraducir(this.traducciones, tag.tipo.nombre, this.localeId));
        }

        return esCoincidencia;
    }
}
